"use client";

import { TextAreaField, TextField } from "@/components/tool-kit/primitives";

/*
 * A field list driven by data: each entry names an input key, the strings keys of its
 * label and placeholder, and whether it is one line or many. The research-workflow
 * generators collect a dozen or more text answers each, and listing them as data keeps
 * the widgets about their documents rather than about repeated JSX.
 */

/** The keys of `T` whose values are strings. */
export type StringKeys<T> = {
  [K in keyof T]: T[K] extends string ? K : never;
}[keyof T] &
  string;

/** `[input key, label key, placeholder key or null, multiline]`. */
export type FieldSpec<I, S> = readonly [
  StringKeys<I>,
  StringKeys<S>,
  StringKeys<S> | null,
  boolean,
];

export function SpecFields<I, S>({
  prefix,
  specs,
  input,
  strings,
  onChange,
}: {
  /** Short id prefix, unique on the page: `dbf` → `dbf-purpose`. */
  prefix: string;
  specs: readonly FieldSpec<I, S>[];
  input: I;
  strings: S;
  onChange: (key: StringKeys<I>, value: string) => void;
}) {
  return (
    <>
      {specs.map(([key, label, placeholder, multiline]) => {
        const common = {
          id: `${prefix}-${key}`,
          label: String(strings[label]),
          onChange: (value: string) => onChange(key, value),
          placeholder: placeholder ? String(strings[placeholder]) : undefined,
          value: String(input[key] ?? ""),
        };
        return multiline ? (
          <TextAreaField key={key} prose rows={3} {...common} />
        ) : (
          <TextField key={key} {...common} />
        );
      })}
    </>
  );
}
